import { PageHero } from "@/components/PageHero";
import { PageSkeleton } from "@/components/Skeletons";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { toast } from "sonner";
import { shareViaText, textShareToast } from "@/lib/sms";
import {
    Stethoscope, ChevronDown, Loader2, Share2, Copy, Check, Mail,
    MessageSquare, Lock, Printer, CloudOff, CloudUpload, ShieldCheck,
    AlertTriangle, FileText, ClipboardList, ArrowRight,
} from "lucide-react";
import { useCountry } from "@/lib/country";
import { useAuth } from "@/lib/auth";
import { track, EVENTS } from "@/lib/analytics";
import { GuideProviderFinder } from "@/components/GuideProviderFinder";

/* /guide — GUIDE-program flow, US-only.
 *
 * Eligibility lives at the top so caregivers learn whether they qualify
 * before investing time. Once they answer (and especially if they qualify)
 * the rest of the screeners unfold below. Sharing — the whole point — is
 * the last step and gives a single read-only link to send a GUIDE provider.
 *
 * Why a dedicated section: the GUIDE flow is one connected ritual
 * (eligibility → screeners → share). It used to live mixed in with the
 * Documents page and competed with legal documents/uploads. Moving it
 * here keeps Documents calm and gives GUIDE the room it needs.
 */
export default function Guide() {
    const { country } = useCountry();
    const { user } = useAuth();
    const isUS = user?.role === "admin" || (country || "US").toUpperCase() === "US";

    if (!isUS) {
        return (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-center" data-testid="guide-not-available">
                <Stethoscope size={32} strokeWidth={1.6} className="text-teal-dark mx-auto mb-3" />
                <h1 className="font-heading text-3xl text-[#12776F]">GUIDE is a US-only program.</h1>
                <p className="mt-3 text-base text-[#4C5551] leading-relaxed">
                    GUIDE is Medicare&apos;s dementia care navigation program. It&apos;s only available
                    in the United States. We&apos;ll surface country-specific care navigation for you instead.
                </p>
                <Link to="/dashboard" className="ml-btn-primary inline-flex items-center gap-2 mt-6">
                    Back to dashboard
                </Link>
            </div>
        );
    }

    return <GuideHome />;
}

function GuideHome() {
    const sid = getSessionId();
    const [loading, setLoading] = useState(true);
    const [forms, setForms] = useState([]);
    const [responses, setResponses] = useState({});
    const [scoreRows, setScoreRows] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [patientName, setPatientName] = useState("");
    const [caregiverName, setCaregiverName] = useState("");
    const [expandedForm, setExpandedForm] = useState(null);
    const [share, setShare] = useState(null);
    const [shareError, setShareError] = useState("");
    const [sharing, setSharing] = useState(false);
    const [saveStatus, setSaveStatus] = useState("idle");
    const [carriedOver, setCarriedOver] = useState(0);
    const lastSentRef = useRef("");
    const initialLoadDoneRef = useRef(false);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [r, savedResp, ms] = await Promise.all([
                    api.get("/documents/guide/forms"),
                    api.get(`/documents/guide/responses/${sid}`).catch(() => ({ data: { responses: {}, score_rows: [] } })),
                    api.get(`/my-story/${sid}`).catch(() => ({ data: null })),
                ]);
                if (!active) return;
                setForms(r.data?.forms || []);
                const respData = savedResp.data?.responses || {};
                setResponses(respData);
                setScoreRows(savedResp.data?.score_rows || []);
                lastSentRef.current = JSON.stringify(respData);
                // Phase 4 data reuse — carry over answers from the public
                // /tools/guide-check pre-screen (same engine, same ids) when
                // the eligibility form hasn't been started yet.
                const eligAnswers = (respData.guide_eligibility || {}).answers || {};
                if (!Object.keys(eligAnswers).length) {
                    try {
                        const pre = JSON.parse(localStorage.getItem("ml_guide_prescreen") || "null");
                        const preAnswers = pre?.answers || {};
                        if (Object.keys(preAnswers).length) {
                            setResponses(prev => ({
                                ...prev,
                                guide_eligibility: {
                                    ...(prev.guide_eligibility || {}),
                                    answers: { ...preAnswers },
                                },
                            }));
                            setCarriedOver(Object.keys(preAnswers).length);
                        }
                    } catch { /* private mode / bad JSON */ }
                }
                const ms_data = ms.data;
                if (ms_data) {
                    setPatientName((ms_data.loved_one?.name) || "");
                    setCaregiverName((ms_data.caregiver?.name) || "");
                }
            } finally {
                if (active) {
                    setLoading(false);
                    initialLoadDoneRef.current = true;
                }
            }
        })();
        return () => { active = false; };
    }, [sid]);

    // Debounced autosave — identical to the older Documents folder, kept
    // because GUIDE pre-visit forms can take hours across multiple
    // sittings and we don't want a single network hiccup to lose work.
    useEffect(() => {
        if (loading || !initialLoadDoneRef.current) return;
        const serialised = JSON.stringify(responses);
        if (serialised === lastSentRef.current) return;
        const t = setTimeout(async () => {
            const attempt = async (delay) => {
                setSaveStatus("saving");
                try {
                    const r = await api.put(`/documents/guide/responses/${sid}`, {
                        session_id: sid, responses,
                    });
                    setScoreRows(r.data?.score_rows || []);
                    lastSentRef.current = serialised;
                    setSaveStatus("saved");
                    return true;
                } catch (e) {
                    if (e?.response?.status === 429 && delay < 8000) {
                        await new Promise(res => setTimeout(res, delay));
                        return attempt(delay * 2);
                    }
                    setSaveStatus("error");
                    return false;
                }
            };
            await attempt(800);
        }, 1500);
        return () => clearTimeout(t);
    }, [responses, sid, loading]);

    const setAnswer = useCallback((formId, itemId, value) => {
        setResponses(prev => ({
            ...prev,
            [formId]: {
                ...(prev[formId] || {}),
                answers: { ...((prev[formId] || {}).answers || {}), [itemId]: value },
            },
        }));
        // Fire GUIDE_ELIGIBILITY_STARTED the first time a caregiver answers
        // any eligibility question (one event per browser session).
        if (formId === "guide_eligibility" && typeof window !== "undefined"
            && !sessionStorage.getItem("ml_ga_guide_started")) {
            sessionStorage.setItem("ml_ga_guide_started", "1");
            track(EVENTS.GUIDE_ELIGIBILITY_STARTED, { session_id: sid });
        }
        setShare(null);
    }, [sid]);

    const setFreeText = useCallback((formId, fieldId, value) => {
        setResponses(prev => ({
            ...prev,
            [formId]: {
                ...(prev[formId] || {}),
                free_text: { ...((prev[formId] || {}).free_text || {}), [fieldId]: value },
            },
        }));
        setShare(null);
    }, []);

    const toggle = (id) => {
        setSelected(s => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
        setShare(null);
    };

    const eligibilityForm = useMemo(() => forms.find(f => f.id === "guide_eligibility"), [forms]);
    const otherForms = useMemo(() => forms.filter(f => f.id !== "guide_eligibility"), [forms]);
    const eligibilityRow = useMemo(() => scoreRows.find(r => r.id === "guide_eligibility"), [scoreRows]);

    // GUIDE_ELIGIBILITY_COMPLETED — fires once the backend returns an
    // eligibility verdict (qualifies / doesn't / needs follow-up). Gated
    // with sessionStorage so it counts as a conversion exactly once.
    useEffect(() => {
        if (!eligibilityRow || typeof window === "undefined") return;
        if (sessionStorage.getItem("ml_ga_guide_completed")) return;
        const verdict = eligibilityRow.summary || eligibilityRow.result || eligibilityRow.score;
        if (verdict === undefined || verdict === null) return;
        sessionStorage.setItem("ml_ga_guide_completed", "1");
        track(EVENTS.GUIDE_ELIGIBILITY_COMPLETED, {
            verdict: String(verdict).slice(0, 40),
        });
    }, [eligibilityRow]);

    const selectAll = () => {
        // "Share everything" = the eligibility form + every other form they touched.
        const ids = forms.map(f => f.id);
        setSelected(new Set(ids));
        setShare(null);
    };
    const clearAll = () => { setSelected(new Set()); setShare(null); };

    const createShare = async () => {
        if (selected.size === 0) { toast("Tick at least one form to include."); return; }
        setSharing(true);
        setShareError("");
        const attempt = async (delay) => {
            try {
                const r = await api.post("/documents/guide/share", {
                    form_ids: Array.from(selected),
                    session_id: sid,
                    patient_name: patientName,
                    caregiver_name: caregiverName,
                });
                setShare(r.data);
                toast.success("Share link ready.");
                track(EVENTS.GUIDE_PACKET_SHARED, {
                    form_count: selected.size,
                    forms: Array.from(selected).join(","),
                });
                return true;
            } catch (e) {
                if (e?.response?.status === 429 && delay < 8000) {
                    await new Promise(res => setTimeout(res, delay));
                    return attempt(delay * 2);
                }
                const msg = e?.response?.status === 429
                    ? "We're being rate-limited — please wait a few seconds and try again."
                    : (e?.response?.data?.detail || "Couldn't create a share link just now.");
                setShareError(msg);
                toast.error(msg);
                return false;
            }
        };
        try {
            await attempt(800);
        } finally {
            setSharing(false);
        }
    };

    if (loading) {
        return <PageSkeleton cards={3} testid="guide-loading" />;
    }

    const clinicalRows = scoreRows.filter(r => r.id !== "guide_eligibility" && r.score !== null && r.score !== undefined);
    const keyFlags = clinicalRows.map(r => r.flag).filter(Boolean);

    return (
        <div className="max-w-5xl space-y-8 animate-fade-in-up" data-testid="guide-page">
            <PageHero
                kicker="GUIDE"
                kickerIcon={Stethoscope}
                title="Medicare GUIDE — see if you qualify, then prep one packet."
                subtitle="Start with the eligibility check. If you qualify, fill out the pre-visit screeners at your own pace, then send a single read-only link to your GUIDE provider — no scanning, no faxing, no retyping."
            />

            <GuideProgressRing forms={forms} responses={responses} />

            {/* TAB 1 — Eligibility (always on top) */}
            {eligibilityForm && (
                <>
                    {carriedOver > 0 && (
                        <div className="ml-card p-3.5 bg-teal-soft/40 !border-teal-light text-sm text-[#1F2624] flex items-start gap-2" data-testid="guide-prescreen-carryover">
                            <span aria-hidden="true" className="mt-0.5 text-teal-dark">✓</span>
                            <p>
                                We carried over <strong>{carriedOver} answer{carriedOver === 1 ? "" : "s"}</strong> from
                                your 1-minute eligibility check — review them below and finish the remaining questions.
                            </p>
                        </div>
                    )}
                    <EligibilityPanel
                        form={eligibilityForm}
                        response={responses[eligibilityForm.id] || { answers: {}, free_text: {} }}
                        onAnswer={(itemId, v) => setAnswer(eligibilityForm.id, itemId, v)}
                        onFreeText={(fieldId, v) => setFreeText(eligibilityForm.id, fieldId, v)}
                        row={eligibilityRow}
                        saveStatus={saveStatus}
                    />
                </>
            )}

            {/* TAB 2 — Other screeners */}
            <section className="ml-card p-5 md:p-6 bg-white" data-testid="guide-screeners-section">
                <div className="flex items-start justify-between gap-3 mb-1">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <ClipboardList size={18} className="text-teal-dark" />
                            <h2 className="font-heading text-2xl text-[#12776F]">Pre-visit screeners</h2>
                        </div>
                        <p className="text-sm text-[#4C5551] max-w-2xl">
                            These are the same forms a GUIDE provider would normally ask you to fill out
                            at the first visit. Doing them ahead of time means more talking, less paperwork at the clinic.
                        </p>
                    </div>
                    <SaveStatusChip status={saveStatus} />
                </div>

                <div className="flex flex-wrap gap-2 mt-4 mb-3">
                    <button type="button" onClick={selectAll} data-testid="guide-select-all" className="px-4 py-1.5 min-h-[44px] rounded-2xl text-xs font-medium bg-white border border-cream-dark text-[#1F2624] hover:bg-cream-dark/30">
                        Include all in share
                    </button>
                    <button type="button" onClick={clearAll} data-testid="guide-clear-all" className="px-4 py-1.5 min-h-[44px] rounded-2xl text-xs font-medium bg-white border border-cream-dark text-[#1F2624] hover:bg-cream-dark/30">
                        Clear
                    </button>
                    <span className="text-xs text-[#6E7A74] inline-flex items-center gap-1 ml-1">
                        <CloudUpload size={11} /> Answers save automatically — come back any time.
                    </span>
                </div>

                <ul className="space-y-3" data-testid="guide-forms-list">
                    {otherForms.map(f => {
                        const checked = selected.has(f.id);
                        const expanded = expandedForm === f.id;
                        const row = scoreRows.find(r => r.id === f.id);
                        const formResp = responses[f.id] || { answers: {}, free_text: {} };
                        return (
                            <li key={f.id}
                                data-testid={`guide-form-row-${f.id}`}
                                className={`rounded-2xl border transition-colors overflow-hidden ${checked ? "bg-teal-soft/40 border-teal" : "bg-cream-light border-cream-dark"}`}
                            >
                                <div className="flex items-start gap-3 p-3 md:p-4">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggle(f.id)}
                                        data-testid={`guide-form-checkbox-${f.id}`}
                                        className="mt-1 w-4 h-4 accent-teal-dark shrink-0"
                                        aria-label={`Include ${f.title} in share`}
                                    />
                                    <FileText size={18} strokeWidth={1.6} className="text-stage_late-dark mt-0.5 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-[#1F2624]">{f.title}</p>
                                        <p className="text-sm text-[#4C5551] mt-0.5">{f.purpose}</p>
                                        <p className="text-xs text-[#6E7A74] mt-1">
                                            GUIDE {f.guide_map} · {f.audience}
                                            {f.max_score ? ` · max ${f.max_score}` : ""}
                                        </p>
                                        {row?.score !== null && row?.score !== undefined && (
                                            <p className="mt-1.5 text-xs inline-flex items-center gap-1.5 px-2 py-0.5 rounded-2xl bg-sage-light border border-sage-soft text-sage-dark" data-testid={`guide-form-saved-${f.id}`}>
                                                <Check size={11} /> Saved · score will be included
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setExpandedForm(expanded ? null : f.id)}
                                        data-testid={`guide-form-toggle-${f.id}`}
                                        className="text-xs font-medium px-4 py-1.5 min-h-[44px] rounded-2xl bg-white border border-cream-dark text-[#1F2624] hover:bg-cream-dark/30 inline-flex items-center gap-1 shrink-0"
                                    >
                                        {expanded ? "Hide" : "Fill out"}
                                        <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                                    </button>
                                </div>
                                {expanded && (
                                    <FormEditor
                                        form={f}
                                        response={formResp}
                                        onAnswer={(itemId, val) => setAnswer(f.id, itemId, val)}
                                        onFreeText={(fieldId, val) => setFreeText(f.id, fieldId, val)}
                                    />
                                )}
                            </li>
                        );
                    })}
                </ul>
            </section>

            {clinicalRows.length > 0 && (
                <ClinicalScoresPanel rows={clinicalRows} flags={keyFlags} />
            )}

            {/* TAB 3 — Share */}
            <section className="ml-card p-5 md:p-6 bg-teal-soft/30 border border-teal-light" data-testid="guide-share-section">
                <div className="flex items-center gap-2 mb-1">
                    <Share2 size={18} className="text-teal-dark" />
                    <h2 className="font-heading text-2xl text-[#12776F]">Share with my GUIDE provider</h2>
                </div>
                <p className="text-sm text-[#4C5551] max-w-2xl">
                    Generate one private read-only link bundling every form you ticked above.
                    Send it to your GUIDE provider via email, text, or copy/paste. Good for 30 days.
                </p>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                        <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Patient name (for the provider)</span>
                        <input
                            value={patientName}
                            onChange={e => setPatientName(e.target.value)}
                            data-testid="guide-patient-name"
                            className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                            placeholder="e.g. Marie Johnson"
                        />
                    </label>
                    <label className="block">
                        <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Caregiver name</span>
                        <input
                            value={caregiverName}
                            onChange={e => setCaregiverName(e.target.value)}
                            data-testid="guide-caregiver-name"
                            className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                            placeholder="e.g. Sam"
                        />
                    </label>
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={createShare}
                        disabled={sharing || selected.size === 0}
                        data-testid="guide-share-create"
                        className="px-5 py-2.5 rounded-2xl bg-teal-dark text-white text-sm font-medium hover:bg-teal disabled:opacity-60 inline-flex items-center gap-2"
                    >
                        {sharing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
                        Share with my GUIDE provider
                    </button>
                    <button
                        type="button"
                        onClick={() => window.print()}
                        data-testid="guide-print"
                        className="px-5 py-2.5 rounded-2xl bg-white border border-cream-dark text-[#1F2624] text-sm font-medium hover:bg-cream-dark/30 inline-flex items-center gap-2"
                    >
                        <Printer size={14} /> Print / Save as PDF
                    </button>
                </div>

                {selected.size === 0 && (
                    <p className="mt-3 text-xs text-[#6E7A74]" data-testid="guide-share-empty-hint">
                        Tick at least one form above (or click <em>Include all in share</em>) to enable sharing.
                    </p>
                )}

                {shareError && (
                    <div className="mt-3 rounded-2xl px-4 py-3 bg-yellow-light border border-yellow text-sm text-yellow-dark" data-testid="guide-share-error">
                        <strong>Hmm:</strong> {shareError}
                    </div>
                )}

                {share?.token && (
                    <DocumentShareBlock
                        token={share.token}
                        patientName={patientName}
                        caregiverName={caregiverName}
                        count={share.form_ids?.length || 0}
                    />
                )}
            </section>

            {/* Find a GUIDE provider — the last beat. After the caregiver
                has eligibility + screeners + share link, the next question
                is always "OK, who do I send this to?". Deep-link straight
                to the GUIDE-providers card in Resources (anchor scrolls it
                into view) so they don't have to hunt for it. */}
            <Link
                to="/help-finder#guide-providers"
                data-testid="guide-find-provider-pointer"
                className="ml-card ml-card-hover p-5 md:p-6 flex items-start gap-4 bg-stage_early/30 !border-stage_early-soft hover:!border-stage_early-dark transition-colors group"
            >
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-white flex items-center justify-center">
                    <Stethoscope strokeWidth={1.6} className="text-stage_early-dark" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-wider font-semibold text-stage_early-dark">Next step</p>
                    <h2 className="mt-0.5 text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                        Find a GUIDE provider — plus home health, hospice &amp; respite near you
                    </h2>
                    <p className="mt-1 text-sm text-[#4C5551] leading-relaxed">
                        ~390 health systems across the U.S. participate in Medicare&apos;s GUIDE program.
                        We&apos;ve linked the official CMS directory, organized by state, plus the
                        GUIDE-supported services your loved one might need next — home health, hospice,
                        respite care, and adult day. Pick your state to filter everything.
                    </p>
                </div>
                <ArrowRight size={20} strokeWidth={1.6} className="text-stage_early-dark mt-2 shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>
        </div>
    );
}

/* ---------------- Eligibility panel ----------------
 * The single most important moment in this whole page: did they qualify?
 * Render the eligibility form inline with a live verdict banner so the
 * caregiver doesn't have to scroll to a separate scores table.
 */
function EligibilityPanel({ form, response, onAnswer, onFreeText, row, saveStatus }) {
    const answered = !!(row && (row.score !== null && row.score !== undefined));
    const band = row?.band;
    const eligible = !!(band && band.startsWith("Likely eligible"));
    const notEligible = !!(band && band.startsWith("Likely NOT"));

    return (
        <section
            className={`ml-card p-5 md:p-6 ${eligible ? "bg-stage_early/30 border-stage_early" : answered ? "bg-yellow-light/50 border-yellow" : "bg-teal-soft/40 border-teal-light"}`}
            data-testid="guide-eligibility-panel"
        >
            <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${eligible ? "bg-stage_early text-white" : answered ? "bg-yellow text-yellow-dark" : "bg-teal text-white"}`}>
                        <ShieldCheck size={18} strokeWidth={1.7} />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Step 1</p>
                        <h2 className="font-heading text-2xl text-[#12776F]">Are you eligible for GUIDE?</h2>
                        <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                            {form.purpose}
                        </p>
                    </div>
                </div>
                <SaveStatusChip status={saveStatus} />
            </div>

            {/* Verdict banner — only after they've answered something */}
            {answered && (
                <div
                    data-testid="guide-eligibility-verdict"
                    className={`mt-4 rounded-2xl p-4 border ${eligible ? "bg-white border-stage_early" : "bg-white border-yellow"}`}
                >
                    <p className={`text-sm font-semibold ${eligible ? "text-stage_early-dark" : "text-yellow-dark"}`}>
                        {band}
                        {row?.score !== null && row?.score !== undefined && (
                            <span className="ml-2 font-normal text-[#4C5551]">
                                · {row.score} of {form.max_score} yes
                            </span>
                        )}
                    </p>
                    {row?.flag && (
                        <p className="text-xs text-[#4C5551] mt-1 inline-flex items-center gap-1">
                            <AlertTriangle size={11} /> {row.flag}
                        </p>
                    )}
                    {eligible && (
                        <p className="text-sm text-[#4C5551] mt-2 leading-relaxed">
                            Looks like a match. Next: fill out the pre-visit screeners below — they save automatically — then share one link with your GUIDE provider.
                        </p>
                    )}
                    {eligible && <GuideProviderFinder basePath="/help-finder" source="guide-eligibility-provider-finder" />}
                    {answered && notEligible && (
                        <p className="text-sm text-[#4C5551] mt-2 leading-relaxed">
                            You may not qualify for GUIDE yet, but the screeners below are still useful at any neurologist or memory-clinic visit — and circumstances change (e.g. switching from Medicare Advantage to Original Medicare).
                        </p>
                    )}
                </div>
            )}

            <FormEditor
                form={form}
                response={response}
                onAnswer={onAnswer}
                onFreeText={onFreeText}
            />
        </section>
    );
}

/* ---------------- Progress ring ----------------
 * "3 of 16 forms started" — returning caregivers see their progress at a
 * glance. A form counts as started once any answer or free-text is saved.
 */
function GuideProgressRing({ forms, responses }) {
    const total = forms.length;
    const touched = forms.filter(f => {
        const r = responses[f.id] || {};
        const a = Object.values(r.answers || {}).some(v => v !== null && v !== undefined && v !== "");
        const t = Object.values(r.free_text || {}).some(v => String(v || "").trim());
        return a || t;
    }).length;
    if (!total) return null;
    const R = 26;
    const C = 2 * Math.PI * R;
    const pct = touched / total;
    const copy = touched === 0
        ? "Start with the eligibility check below — everything saves automatically, so you can return anytime."
        : touched < total
            ? "Nice progress — your answers save automatically. Pick up right where you left off."
            : "Every form has answers. Review and share your packet with a GUIDE provider below.";
    return (
        <section className="ml-card p-4 md:p-5 flex items-center gap-4 bg-white" data-testid="guide-progress-ring">
            <svg width="68" height="68" viewBox="0 0 68 68" className="shrink-0" role="img"
                aria-label={`${touched} of ${total} forms started`}>
                <circle cx="34" cy="34" r={R} fill="none" stroke="#EDE6D6" strokeWidth="7" />
                <circle cx="34" cy="34" r={R} fill="none" stroke="#2C7A8C" strokeWidth="7" strokeLinecap="round"
                    strokeDasharray={C} strokeDashoffset={C * (1 - pct)}
                    transform="rotate(-90 34 34)" style={{ transition: "stroke-dashoffset .6s ease" }} />
                <text x="34" y="39" textAnchor="middle" fontSize="14" fontWeight="700" fill="#2C7A8C">{touched}/{total}</text>
            </svg>
            <div className="min-w-0">
                <p className="font-medium text-[#1F2624] text-sm" data-testid="guide-progress-count">
                    {touched} of {total} forms started
                </p>
                <p className="text-xs text-[#6E7A74] mt-0.5 leading-relaxed">{copy}</p>
            </div>
        </section>
    );
}

/* ---------------- FormEditor (reused as-is) ---------------- */
function FormEditor({ form, response, onAnswer, onFreeText }) {
    const answers = response?.answers || {};
    const freeText = response?.free_text || {};
    const items = form.items || [];
    const freeTextFields = form.free_text_fields || [];
    return (
        <div className="px-1 pb-1 pt-3" data-testid={`guide-form-editor-${form.id}`}>
            {items.length > 0 && (
                <div className="space-y-3">
                    {items.map(item => (
                        <QuestionItem
                            key={item.id}
                            formId={form.id}
                            item={item}
                            value={answers[item.id]}
                            onChange={(v) => onAnswer(item.id, v)}
                        />
                    ))}
                </div>
            )}
            {freeTextFields.length > 0 && (
                <div className="space-y-3 mt-4 pt-4 border-t border-cream-dark">
                    {freeTextFields.map(ft => (
                        <label key={ft.id} className="block">
                            <span className="block text-sm font-medium text-[#1F2624] mb-1">{ft.label}</span>
                            {ft.multiline ? (
                                <textarea
                                    rows={3}
                                    value={freeText[ft.id] || ""}
                                    onChange={(e) => onFreeText(ft.id, e.target.value)}
                                    data-testid={`guide-form-${form.id}-text-${ft.id}`}
                                    className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                    placeholder="Type here…"
                                />
                            ) : (
                                <input
                                    type="text"
                                    value={freeText[ft.id] || ""}
                                    onChange={(e) => onFreeText(ft.id, e.target.value)}
                                    data-testid={`guide-form-${form.id}-text-${ft.id}`}
                                    className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                    placeholder="Type here…"
                                />
                            )}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function QuestionItem({ formId, item, value, onChange }) {
    const scale = item.scale || [];
    const useSelect = scale.length > 5;
    return (
        <div className="p-3 rounded-2xl bg-white border border-cream-dark" data-testid={`guide-form-${formId}-item-${item.id}`}>
            <p className="text-sm text-[#1F2624] mb-2">{item.label}</p>
            {useSelect ? (
                <select
                    value={value ?? ""}
                    onChange={(e) => {
                        const v = e.target.value;
                        const numericTry = Number(v);
                        onChange(v === "" ? "" : (Number.isFinite(numericTry) && !Number.isNaN(numericTry) && /^-?\d+(\.\d+)?$/.test(v) ? numericTry : v));
                    }}
                    data-testid={`guide-form-${formId}-select-${item.id}`}
                    className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                >
                    <option value="">Choose…</option>
                    {scale.map(opt => (
                        <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>
                    ))}
                </select>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {scale.map(opt => {
                        const selected = String(value) === String(opt.value);
                        return (
                            <button
                                type="button"
                                key={String(opt.value)}
                                onClick={() => onChange(opt.value)}
                                data-testid={`guide-form-${formId}-opt-${item.id}-${opt.value}`}
                                className={`px-4 py-1.5 min-h-[44px] rounded-2xl text-xs font-medium border transition-colors ${selected ? "bg-teal-dark text-white border-teal-dark" : "bg-white border-cream-dark text-[#1F2624] hover:bg-cream-dark/30"}`}
                                aria-pressed={selected}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ---------------- Clinical scores ---------------- */
function ClinicalScoresPanel({ rows, flags }) {
    const [open, setOpen] = useState(false);
    return (
        <section className="ml-card p-0 bg-sage-light/60 border border-sage-soft overflow-hidden" data-testid="guide-clinical-scores">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                data-testid="guide-clinical-scores-toggle"
                className="w-full flex items-center justify-between gap-3 px-5 md:px-6 py-4 hover:bg-sage-light/80 transition-colors text-left"
            >
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-sage text-white flex items-center justify-center shrink-0">
                        <Stethoscope size={18} strokeWidth={1.7} />
                    </div>
                    <div>
                        <h2 className="text-xl md:text-2xl font-heading font-medium text-[#12776F]">Clinical scores</h2>
                        <p className="text-sm text-[#4C5551] mt-0.5">
                            {rows.length} screener{rows.length === 1 ? "" : "s"} scored — included automatically when you share.
                        </p>
                    </div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-2xl bg-white border border-sage-soft text-sage-dark shrink-0">
                    {open ? "Hide" : "Take a peek"}
                    <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </span>
            </button>
            {open && (
                <div className="px-5 md:px-6 pb-5">
                    {flags.length > 0 && (
                        <div className="mb-4 rounded-2xl bg-yellow-light border border-yellow p-4" data-testid="guide-clinical-flags">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle size={16} className="text-yellow-dark" />
                                <p className="text-sm uppercase tracking-wider font-semibold text-yellow-dark">Things to mention ({flags.length})</p>
                            </div>
                            <ul className="space-y-1 text-sm text-[#1F2624]">
                                {flags.map((f, i) => (<li key={i}>• {f}</li>))}
                            </ul>
                        </div>
                    )}
                    <div className="overflow-x-auto rounded-2xl bg-white border border-sage-soft">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                                    <th className="py-2 px-3 font-semibold">Screener</th>
                                    <th className="py-2 px-3 font-semibold">Score</th>
                                    <th className="py-2 px-3 font-semibold">Band</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.id} className={`border-b border-cream-light ${r.flag ? "bg-yellow-light/40" : ""}`} data-testid={`guide-clinical-row-${r.id}`}>
                                        <td className="py-2.5 px-3 text-[#1F2624]">{r.title}</td>
                                        <td className="py-2.5 px-3 font-mono text-[#1F2624]">
                                            {formatScore(r.score)}
                                            {r.max_score !== null && r.max_score !== undefined && typeof r.max_score === "number" && (
                                                <span className="text-[#6E7A74]"> / {r.max_score}</span>
                                            )}
                                        </td>
                                        <td className="py-2.5 px-3 text-[#4C5551]">{r.band || <span className="text-[#6E7A74]">—</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </section>
    );
}

function formatScore(score) {
    if (score === null || score === undefined || score === "") return <span className="text-[#6E7A74]">—</span>;
    if (typeof score === "number") return Number.isInteger(score) ? score : score.toFixed(1);
    return String(score);
}

/* ---------------- Share output ---------------- */
function DocumentShareBlock({ token, patientName, caregiverName, count }) {
    const [copied, setCopied] = useState(false);
    const url = `${window.location.origin}/share/guide-screeners/${token}`;
    const subject = patientName ? `GUIDE pre-visit screeners for ${patientName}` : "GUIDE pre-visit screeners";
    const body = [
        "Hi —",
        "",
        `${caregiverName ? caregiverName + " " : ""}selected ${count} GUIDE pre-visit enrollment screener${count === 1 ? "" : "s"}${patientName ? " for " + patientName : ""}. The packet is read-only and good for 30 days:`,
        "",
        url,
        "",
        "Open the link to see the responses and clinical scores. You can also download or print it.",
        "",
        caregiverName ? `— ${caregiverName}` : "— The caregiver",
    ].join("\n");

    const sendEmail = () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        toast.success("Opening your email…");
    };
    const sendSms = async () => {
        const method = await shareViaText(body);
        textShareToast(method, toast);
    };
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast("Copy failed — long-press the link.");
        }
    };
    const nativeShare = async () => {
        if (!navigator.share) { toast("Share isn't supported here — use Email or Copy."); return; }
        try { await navigator.share({ title: subject, text: body, url }); } catch { /* user cancelled */ }
    };

    return (
        <div className="mt-5 rounded-2xl p-5 bg-white border border-cream-dark" data-testid="guide-share-block">
            <div className="flex items-start gap-3 mb-3">
                <Lock size={18} className="text-teal-dark mt-0.5" />
                <div>
                    <p className="text-sm font-medium text-[#1F2624]">Private read-only link · 30 days</p>
                    <p className="text-xs text-[#6E7A74] mt-0.5">
                        Anyone with this link sees the selected forms + clinical scores. You can revoke it anytime.
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
                <button type="button" onClick={sendEmail} data-testid="guide-share-email" className="px-3 py-2 rounded-2xl text-sm bg-teal-dark text-white inline-flex items-center justify-center gap-1.5">
                    <Mail size={14} /> Email
                </button>
                <button type="button" onClick={sendSms} data-testid="guide-share-sms" className="px-3 py-2 rounded-2xl text-sm bg-white border border-cream-dark text-[#1F2624] inline-flex items-center justify-center gap-1.5">
                    <MessageSquare size={14} /> Text
                </button>
                <button type="button" onClick={copy} data-testid="guide-share-copy" className="px-3 py-2 rounded-2xl text-sm bg-coral-light text-coral-dark inline-flex items-center justify-center gap-1.5">
                    {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                </button>
                <button type="button" onClick={nativeShare} data-testid="guide-share-native" className="px-3 py-2 rounded-2xl text-sm bg-white border border-cream-dark text-[#1F2624] inline-flex items-center justify-center gap-1.5">
                    <Share2 size={14} /> Share
                </button>
            </div>
            <input readOnly value={url} data-testid="guide-share-link" className="w-full text-xs px-3 py-2 rounded-2xl bg-white border border-cream-dark truncate" />
        </div>
    );
}

function SaveStatusChip({ status }) {
    if (status === "idle") return null;
    if (status === "saving") {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-teal-dark" data-testid="guide-save-status-saving">
                <CloudUpload size={12} className="animate-pulse" /> saving…
            </span>
        );
    }
    if (status === "saved") {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-sage-dark" data-testid="guide-save-status-saved">
                <Check size={12} /> saved
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 text-xs text-yellow-dark" data-testid="guide-save-status-error">
            <CloudOff size={12} /> couldn&apos;t save — we&apos;ll retry
        </span>
    );
}
